import type {
  NewsFeed,
  NewsList,
  StrapiAuthorItem,
  StrapiCategoryItem,
  StrapiNewsItem,
  StrapiNewsStoryItem,
  StrapiSectionItem,
} from './models';
import type {
  StrapiAuthorVerboseDto,
  StrapiCategoryDto,
  StrapiNewsArticleDto,
  StrapiNewsCardDto,
  StrapiNewsDto,
  StrapiNewsFeedDto,
  StrapiNewsListDto,
  StrapiSectionDto,
} from './types';

// Create a short, sentence-complete description around 150–160 characters
function createDescriptionFromContent(content?: string): string {
  if (!content) return '';

  const MIN = 150;
  const MAX = 160;

  const trimmed = content.trim();
  if (trimmed.length <= MAX) {
    const dotIdx = trimmed.lastIndexOf('.');
    // If there is a dot anywhere, prefer ending at the last sentence
    if (dotIdx !== -1) return trimmed.slice(0, dotIdx + 1).trim();
    return trimmed;
  }

  const firstSlice = trimmed.slice(0, MAX);

  // Prefer a dot in [MIN..MAX]
  for (let i = MAX - 1; i >= MIN; i--) {
    if (firstSlice[i] === '.') {
      return firstSlice.slice(0, i + 1).trim();
    }
  }

  // Otherwise, take the last dot before MIN (shorter but sentence-complete)
  const lastDotBefore = firstSlice.lastIndexOf('.');
  if (lastDotBefore !== -1) {
    return firstSlice.slice(0, lastDotBefore + 1).trim();
  }

  // Final fallback: avoid breaking a word; cut on last space if possible
  const lastSpace = firstSlice.lastIndexOf(' ');
  if (lastSpace > -1) {
    return firstSlice.slice(0, lastSpace).trimEnd();
  }
  return firstSlice.trimEnd();
}

// Ensure timestamp is treated as UTC — the Strapi API returns ISO strings
// without a timezone suffix, which browsers parse as local time (causing
// hydration mismatches and wrong relative times in non-UTC timezones).
function ensureUTC(isoString: string): string {
  if (!isoString.endsWith('Z') && !isoString.includes('+') && !isoString.includes('-', 10)) {
    return isoString + 'Z';
  }
  return isoString;
}

function mapCategoryRefDto(dto?: { i: string; n: string; s: string; isc: boolean }): {
  id: string;
  name: string;
  slug: string;
  isSubcategory: boolean;
} | null {
  if (!dto) return null;
  return {
    id: dto.i,
    name: dto.n,
    slug: dto.s,
    isSubcategory: dto.isc,
  };
}

function mapAuthorDto(
  dto:
    | {
        i: string;
        n: string;
        e: string;
        b: string;
        s: string;
        st: string;
        l: string;
        al: string;
      }
    | null
    | undefined,
): {
  id: string;
  name: string;
  email: string;
  bio: string;
  style: string;
  status: string;
  language: string;
  avatar_link: string;
} {
  if (!dto) {
    // Return default author if dto is null/undefined
    return {
      id: 'unknown',
      name: 'Unknown Author',
      email: '',
      bio: '',
      style: '',
      status: 'active',
      language: 'en',
      avatar_link: '',
    };
  }
  return {
    id: dto.i,
    name: dto.n,
    email: dto.e,
    bio: dto.b,
    style: dto.s,
    status: dto.st,
    language: dto.l,
    avatar_link: dto.al,
  };
}

export function mapStrapiNews(dto: StrapiNewsDto | null | undefined): StrapiNewsItem {
  if (!dto) {
    throw new Error('Cannot map null or undefined StrapiNewsDto');
  }
  const content = dto.d ?? '';
  return {
    id: dto.i,
    title: dto.t ?? '',
    slug: dto.s ?? '',
    content: content,
    language: dto.l ?? 'en',
    author: mapAuthorDto(dto.a ?? null),
    category: mapCategoryRefDto(dto.ct ?? null),
    section: mapCategoryRefDto(dto.sc ?? null),
    subcategory: mapCategoryRefDto(dto.sct ?? null),
    tags: (dto.tg ?? '')
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean),
    // sentence-complete description around 150–160 chars
    description: createDescriptionFromContent(content),
    image: dto.im ?? '',
    commentCount: dto.cc ?? 0,
    eventId: dto.e ?? null,
    // Use full publishedAt ISO timestamp for consistent relative time display
    date: dto.pb ? ensureUTC(dto.pb) : new Date().toISOString(),
    url: dto.src ?? '',
    publishedAt: dto.pb ? ensureUTC(dto.pb) : new Date().toISOString(),
    pair: dto.p ?? null,
    X_Posts: dto.xps ?? null,
    X_Persons: dto.xp ?? null,
  };
}

// ---------------------------------------------------------------------------
// News v2 (i18n) mappers — aliased card/article DTOs -> friendly StrapiNewsItem.
// Card payloads omit content/description/author/category, so those are filled
// with safe defaults; consumers must tolerate empty content/description on
// listing items (the article endpoint carries the full content).
// ---------------------------------------------------------------------------

function mapNewsCardBase(dto: StrapiNewsCardDto): StrapiNewsItem {
  const date = dto.date ? ensureUTC(dto.date) : new Date().toISOString();
  return {
    id: dto.i,
    documentId: dto.di,
    title: dto.t ?? '',
    slug: dto.s ?? '',
    content: '',
    description: dto.ds ?? '',
    language: dto.l ?? 'en',
    author: mapAuthorDto(null),
    category: null,
    section: dto.sc
      ? { id: '', name: dto.sc.n ?? '', slug: dto.sc.s ?? '', isSubcategory: false }
      : null,
    subcategory: null,
    tags: Array.isArray(dto.tg) ? dto.tg.filter(Boolean) : [],
    image: dto.im ?? '',
    commentCount: dto.cc ?? 0,
    eventId: null,
    date,
    publishedAt: date,
    url: '',
    pair: null,
    X_Posts: null,
    X_Persons: null,
  };
}

export function mapNewsCard(dto: StrapiNewsCardDto): StrapiNewsItem {
  return mapNewsCardBase(dto);
}

export function mapNewsArticle(dto: StrapiNewsArticleDto): StrapiNewsItem {
  const content = dto.d ?? '';
  return {
    ...mapNewsCardBase(dto),
    content,
    description: createDescriptionFromContent(content),
    // Article endpoint sends a slim author (id + name); fill the rest with
    // defaults so the StrapiNewsItem author shape stays intact.
    author: dto.au ? { ...mapAuthorDto(null), id: dto.au.i, name: dto.au.n } : mapAuthorDto(null),
    isFallback: dto.fb ?? false,
    requestedLocale: dto.rl,
    availableLocales: (dto.lz ?? []).map((loc) => ({
      locale: loc.l,
      slug: loc.s,
      // Strapi stores section slugs capitalized ("Politics") while every URL
      // on the site uses the lowercase form; normalize here so canonicals,
      // hreflang and the locale switcher all agree with internal links.
      sectionSlug: (loc.ss ?? '').toLowerCase(),
    })),
    // Mirror the legacy mapStrapiNews assignment (X_Posts<-xps, X_Persons<-xp)
    // so the chart / Twitter / key-figure blocks behave exactly as before.
    pair: dto.p ?? null,
    X_Posts: dto.xps ?? null,
    X_Persons: dto.xp ?? null,
  };
}

export function mapNewsFeed(dto: StrapiNewsFeedDto): NewsFeed {
  const mapRail = (items?: StrapiNewsCardDto[]) =>
    (items ?? []).filter((item): item is StrapiNewsCardDto => item != null).map(mapNewsCard);
  return {
    hottest: mapRail(dto?.hottest),
    editorialPicks: mapRail(dto?.editorialPicks),
    featured: mapRail(dto?.featured),
    latest: mapRail(dto?.latest),
    other: mapRail(dto?.other),
  };
}

export function mapNewsList(dto: StrapiNewsListDto): NewsList {
  return {
    items: (dto?.items ?? [])
      .filter((item): item is StrapiNewsCardDto => item != null)
      .map(mapNewsCard),
    page: dto?.page ?? 1,
    nextPage: dto?.nextPage ?? null,
    total: dto?.total ?? 0,
  };
}

export function mapStrapiCategory(dto: StrapiCategoryDto): StrapiCategoryItem {
  return {
    id: dto.i,
    documentId: dto.di ?? dto.i,
    name: dto.n,
    slug: dto.s ?? null,
    description: dto.d ?? null,
    createdAt: new Date(dto.ca),
    updatedAt: new Date(dto.ua),
    publishedAt: new Date(dto.pb),
    level: dto.l ?? null,
    parentCategoryId: dto.pci ?? null,
    // If parentCategoryId exists -> it's a subcategory
    isSubcategory: Boolean(dto.pci),
  } as StrapiCategoryItem;
}

export function mapStrapiAuthor(dto: StrapiAuthorVerboseDto): StrapiAuthorItem {
  return {
    id: dto.id,
    name: dto.name,
    email: dto.email,
    bio: dto.bio,
    style: dto.style,
    status: dto.status,
    language: dto.language,
    avatarLink: dto.avatar_link,
  };
}

export function mapStrapiSection(dto: StrapiSectionDto): StrapiSectionItem {
  return {
    id: dto.id,
    name: dto.name,
    slug: dto.slug,
    isSubcategory: dto.isSubcategory,
  };
}

export function mapStrapiNewsStory(dto: StrapiNewsDto): StrapiNewsStoryItem {
  return {
    id: dto.i,
    title: dto.t,
    slug: dto.s,
    image: dto.im,
    updatedAt: new Date(ensureUTC(dto.pb)),
  };
}
